import React, { useEffect, useMemo, useRef } from 'react';
import { defineAdSlot, displayAdSlot, destroyAdSlot } from '@/utils/gpt';

export interface TrackingAdProps {
  /**
   * Stable identifier for the wrapped content. Applied as the `s5` targeting
   * value and used as the fallback for the ad container's div id.
   */
  id: string;

  /**
   * Whether GAM is enabled for the current tenant (from `gamConfig.enabled`).
   * When false the component is a pure pass-through: children render normally
   * and no GPT calls are made.
   */
  enabled: boolean;

  /** GAM network id for the current tenant (from `gamConfig.networkId`). */
  networkId: string;

  /**
   * Explicit, page-unique id for the hidden slot's DOM container and GPT slot
   * handle. Falls back to `tracking-ad-${id}` when omitted.
   */
  divId?: string;

  /**
   * Explicit ad unit path override. When omitted it is derived as
   * `/${networkId}/free-to-play/lobby`.
   */
  adUnitPath?: string;

  /**
   * Slot-level targeting using the `s1…sn` key scheme (s1 tenant code,
   * s2 lobby identifier, s3 placement name); `s5` is set automatically
   * from `id`.
   */
  targeting?: Record<string, string>;

  /** Content to render. The hidden 1x1 tracking slot is appended after it. */
  children: React.ReactNode;

  /** Optional CSS class applied to the wrapper element. */
  className?: string;

  /**
   * Inline style for the wrapper. Defaults to `{ display: 'contents' }` so the
   * wrapper is layout-transparent and does not disturb the host's flex/grid.
   */
  style?: React.CSSProperties;
}

// 1x1 invisible tracking pixel — never renders a visible advert
const TRACKING_SIZE: [number, number][] = [[1, 1]];

// Matches apps/lobby's TrackingAdProvider basePath="free-to-play/lobby"
const AD_UNIT_BASE_PATH = 'free-to-play/lobby';

const HIDDEN_SLOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
};

/**
 * Build a deterministic string from a targeting map so the slot effect can
 * depend on the *values* rather than the object identity. Callers routinely
 * pass inline `{...}` targeting objects, which change identity on every render;
 * keying on the sorted entries means the slot is only torn down and redefined
 * when a targeting value actually changes.
 */
const targetingKey = (targeting: Record<string, string>): string =>
  JSON.stringify(
    Object.keys(targeting)
      .sort()
      .map(key => [key, targeting[key]])
  );

/**
 * Wraps content in an invisible 1x1 Google Ad Manager slot used purely for
 * impression tracking. No visible advert is rendered.
 *
 * Local fork of the vendored `vendor/ui/src/ads/TrackingAd.tsx` pattern:
 * instead of a TrackingAdProvider context, GAM config is passed explicitly
 * via the `enabled`/`networkId` props (sourced from `useTenant().gamConfig`).
 */
export const TrackingAd: React.FC<TrackingAdProps> = ({
  id,
  enabled,
  networkId,
  divId: providedDivId,
  adUnitPath,
  targeting,
  children,
  className,
  style = { display: 'contents' },
}) => {
  const divId = useMemo(() => providedDivId ?? `tracking-ad-${id}`, [providedDivId, id]);
  const resolvedPath = useMemo(
    () => adUnitPath || `/${networkId}/${AD_UNIT_BASE_PATH}`,
    [adUnitPath, networkId]
  );
  const resolvedTargeting = useMemo(() => ({ ...targeting, s5: id }), [id, targeting]);
  const resolvedTargetingKey = useMemo(() => targetingKey(resolvedTargeting), [resolvedTargeting]);

  // Read the latest targeting from a ref inside the effect so the effect can
  // depend on the stable, value-based key instead of the object identity.
  const targetingRef = useRef(resolvedTargeting);
  targetingRef.current = resolvedTargeting;

  const isDefinedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath: resolvedPath,
        sizes: TRACKING_SIZE,
        divId,
        targeting: targetingRef.current,
      });
      isDefinedRef.current = true;
    }

    displayAdSlot(divId);

    return () => {
      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [enabled, divId, resolvedPath, resolvedTargetingKey]);

  return (
    <div className={className} style={style}>
      {children}
      {enabled ? <div id={divId} style={HIDDEN_SLOT_STYLE} aria-hidden /> : null}
    </div>
  );
};

export default TrackingAd;
